import React, { useEffect, useState } from 'react';
import { pokemonData } from '../data/pokemonData';
import { RefreshCwIcon, AlertCircleIcon, CoinsIcon, ShoppingCartIcon } from 'lucide-react';
export function GuessThatPokemon({
  setPokedexVisibility
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPokemon, setCurrentPokemon] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [userGuess, setUserGuess] = useState('');
  const [feedback, setFeedback] = useState('');
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [notification, setNotification] = useState(null);
  const [usedPokemonIds, setUsedPokemonIds] = useState(new Set());
  const [pokePoints, setPokePoints] = useState(0);
  const [showShopNotification, setShowShopNotification] = useState(false);
  const [hasShownShopNotification, setHasShownShopNotification] = useState(false);
  // Load saved PokePoints on component mount
  useEffect(() => {
    const savedPoints = localStorage.getItem('pokePoints');
    if (savedPoints) {
      setPokePoints(parseInt(savedPoints, 10));
    }
    const notificationShown = localStorage.getItem('shopNotificationShown');
    if (notificationShown) {
      setHasShownShopNotification(true);
    }
  }, []);
  // Get a random Pokémon with weighted probability
  // This function ensures ALL Pokémon from the pokemonData array have a chance to appear
  const getRandomPokemon = () => {
    // Check if all Pokémon have been used - if so, reset the tracking
    if (usedPokemonIds.size >= pokemonData.length - 1) {
      setUsedPokemonIds(new Set());
    }
    // Separate legendary and non-legendary Pokémon
    const legendaryPokemon = pokemonData.filter(pokemon => pokemon.legendary && !usedPokemonIds.has(pokemon.id));
    const regularPokemon = pokemonData.filter(pokemon => !pokemon.legendary && !usedPokemonIds.has(pokemon.id));
    // If we've used all legendary or regular Pokémon, use any remaining unused Pokémon
    let availablePokemon = [...legendaryPokemon, ...regularPokemon];
    // If somehow all Pokémon have been used (shouldn't happen due to the reset above),
    // then just use the full pokemonData array
    if (availablePokemon.length === 0) {
      availablePokemon = pokemonData;
    }
    // Set probability - 15% chance for legendary, 85% chance for regular
    const getLegendary = Math.random() < 0.15;
    let selectedPokemon;
    // Select from the appropriate pool
    if (getLegendary && legendaryPokemon.length > 0) {
      const randomIndex = Math.floor(Math.random() * legendaryPokemon.length);
      selectedPokemon = legendaryPokemon[randomIndex];
    } else if (regularPokemon.length > 0) {
      const randomIndex = Math.floor(Math.random() * regularPokemon.length);
      selectedPokemon = regularPokemon[randomIndex];
    } else {
      // Fallback to any available Pokémon if either category is empty
      const randomIndex = Math.floor(Math.random() * availablePokemon.length);
      selectedPokemon = availablePokemon[randomIndex];
    }
    // Track this Pokémon as used
    setUsedPokemonIds(prev => new Set([...prev, selectedPokemon.id]));
    return selectedPokemon;
  };
  // Initialize with a random Pokémon
  useEffect(() => {
    setCurrentPokemon(getRandomPokemon());
  }, []);
  // Check if we should show the shop notification
  useEffect(() => {
    if (pokePoints >= 10 && !hasShownShopNotification) {
      setShowShopNotification(true);
      setHasShownShopNotification(true);
      localStorage.setItem('shopNotificationShown', 'true');
      setTimeout(() => {
        setShowShopNotification(false);
      }, 5000);
    }
  }, [pokePoints, hasShownShopNotification]);
  const handleToggle = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    // Show notification and hide Pokedex when opening
    if (newIsOpen) {
      setNotification({
        message: 'You are now playing Guess That Pokémon! The pokedex is OFF LIMITS',
        type: 'warning'
      });
      // Auto-dismiss notification after 5 seconds
      setTimeout(() => {
        setNotification(null);
      }, 5000);
      setPokedexVisibility(false);
    } else {
      // When closing the game, reset score and attempts
      setScore(0);
      setAttempts(0);
      setFeedback('');
      setIsRevealed(false);
      setUserGuess('');
      setUsedPokemonIds(new Set()); // Reset used Pokémon tracking
      // Show Pokedex when closing
      setPokedexVisibility(true);
    }
  };
  // Reset game immediately with no delay
  const resetGame = () => {
    // Immediately set a new Pokémon
    setCurrentPokemon(getRandomPokemon());
    setIsRevealed(false);
    setUserGuess('');
    setFeedback('');
  };
  const handleGuess = e => {
    e.preventDefault();
    if (!userGuess.trim()) return;
    setAttempts(attempts + 1);
    // Check if the guess is correct (case insensitive)
    if (userGuess.toLowerCase() === currentPokemon.name.toLowerCase()) {
      const bonusPoints = currentPokemon.legendary ? 3 : 1;
      // Add PokéPoints
      const pointsToAdd = currentPokemon.legendary ? 3 : 1;
      const newPokePoints = pokePoints + pointsToAdd;
      setPokePoints(newPokePoints);
      localStorage.setItem('pokePoints', newPokePoints.toString());
      setFeedback(`Correct! It's ${currentPokemon.name}! ${currentPokemon.legendary ? '(Legendary Pokémon! +3 points, +3 PokéPoints)' : '(+1 point, +1 PokéPoint)'}`);
      setIsRevealed(true);
      setScore(score + bonusPoints);
    } else {
      // Deduct 1 PokéPoint for incorrect guess
      const newPokePoints = Math.max(0, pokePoints - 1); // Ensure points don't go below 0
      setPokePoints(newPokePoints);
      localStorage.setItem('pokePoints', newPokePoints.toString());
      setFeedback(`Not quite! Try again or reveal the answer. (-1 PokéPoint)`);
    }
  };
  const handleReveal = () => {
    // Deduct 1 PokéPoint when giving up
    const newPokePoints = Math.max(0, pokePoints - 1); // Ensure points don't go below 0
    setPokePoints(newPokePoints);
    localStorage.setItem('pokePoints', newPokePoints.toString());
    setFeedback(`It's ${currentPokemon.name}!${currentPokemon.legendary ? ' (Legendary Pokémon)' : ''} (-1 PokéPoint)`);
    setIsRevealed(true);
  };
  const handleNextPokemon = () => {
    resetGame();
  };
  if (!currentPokemon) return <div data-id="element-114">Loading...</div>;
  return <section className="bg-slate-800/70 rounded-lg p-6 backdrop-blur-sm border border-slate-700 shadow-lg relative" data-id="element-115">
      <button className="w-full flex items-center justify-between bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors" onClick={handleToggle} data-id="element-116">
        <span className="text-xl" data-id="element-117">Guess That Pokémon!</span>
        <svg className={`w-6 h-6 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-118">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" data-id="element-119"></path>
        </svg>
      </button>
      {isOpen && <div className="space-y-4 mt-4 p-2 bg-slate-700/60 rounded-lg" data-id="element-120">
          <div className="bg-slate-700/60 rounded-lg p-6 mb-4 text-center" data-id="element-121">
            <div className="mb-6 flex justify-center" data-id="element-122">
              <div className="relative w-48 h-48 bg-slate-600 rounded-lg flex items-center justify-center overflow-hidden" data-id="element-123">
                <img src={currentPokemon.image} alt="Mystery Pokémon" className={`w-40 h-40 object-contain transition-all duration-300 ${isRevealed ? 'filter-none' : 'brightness-0'}`} data-id="element-124" />
              </div>
            </div>
            {!isRevealed ? <form onSubmit={handleGuess} className="mb-4" data-id="element-125">
                <div className="flex flex-col sm:flex-row gap-2 justify-center" data-id="element-126">
                  <input type="text" value={userGuess} onChange={e => setUserGuess(e.target.value)} placeholder="Who's that Pokémon?" className="bg-slate-600 text-white px-4 py-2 rounded-lg border border-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-127" />
                  <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors" data-id="element-128">
                    Guess!
                  </button>
                </div>
                {feedback && <p className="mt-2 text-yellow-300" data-id="element-129">{feedback}</p>}
                <button type="button" onClick={handleReveal} className="mt-4 text-gray-300 hover:text-white underline" data-id="element-130">
                  I give up, reveal the Pokémon
                </button>
              </form> : <div className="mb-4" data-id="element-131">
                <p className="text-xl font-semibold text-yellow-300 mb-2" data-id="element-132">
                  {feedback}
                </p>
                <p className="text-gray-300 mb-4" data-id="element-133">
                  {currentPokemon.description}
                </p>
                <div className="flex justify-center gap-2 mb-2" data-id="element-134">
                  {currentPokemon.types.map(type => <span key={type} className={`px-3 py-1 rounded-full text-sm font-medium text-white ${getTypeColor(type)}`} data-id="element-135">
                      {type}
                    </span>)}
                </div>
                <button onClick={handleNextPokemon} className="bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg transition-colors flex items-center mx-auto" data-id="element-136">
                  <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-137" />
                  Next Pokémon
                </button>
              </div>}
          </div>
          <div className="bg-slate-700/40 rounded-lg p-4 text-center" data-id="element-138">
            <div className="flex justify-center gap-8" data-id="element-139">
              <div data-id="element-140">
                <p className="text-gray-400 text-sm" data-id="element-141">Score</p>
                <p className="text-2xl font-bold text-green-400" data-id="element-142">{score}</p>
              </div>
              <div data-id="element-143">
                <p className="text-gray-400 text-sm" data-id="element-144">Attempts</p>
                <p className="text-2xl font-bold text-blue-400" data-id="element-145">{attempts}</p>
              </div>
              <div data-id="element-146">
                <p className="text-gray-400 text-sm" data-id="element-147">PokéPoints</p>
                <p className="text-2xl font-bold text-yellow-400 flex items-center justify-center" data-id="element-148">
                  <CoinsIcon className="w-4 h-4 mr-1" data-id="element-149" />
                  {pokePoints}
                </p>
              </div>
              {attempts > 0 && <div data-id="element-150">
                  <p className="text-gray-400 text-sm" data-id="element-151">Success Rate</p>
                  <p className="text-2xl font-bold text-yellow-400" data-id="element-152">
                    {Math.round(score / attempts * 100)}%
                  </p>
                </div>}
            </div>
            <p className="text-xs text-gray-400 mt-2" data-id="element-153">
              Tip: All {pokemonData.length} Pokémon from the Pokédex can appear
              in this game!
            </p>
            <p className="text-xs text-gray-400" data-id="element-154">
              Legendary Pokémon are rare and worth more points and PokéPoints!
            </p>
          </div>
        </div>}
      {/* Bottom notifications */}
      {notification && <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-up" data-id="element-155">
          <div className="bg-amber-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center max-w-md" data-id="element-156">
            <AlertCircleIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-157" />
            <p data-id="element-158">{notification.message}</p>
          </div>
        </div>}
      {/* Shop notification */}
      {showShopNotification && <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in-up" data-id="element-159">
          <div className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center max-w-md" data-id="element-160">
            <ShoppingCartIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-161" />
            <p data-id="element-162">Don't forget to check the shop!</p>
          </div>
        </div>}
    </section>;
}
// Helper function to get the background color for a type
function getTypeColor(type) {
  const typeColors = {
    Normal: 'bg-gray-400',
    Fire: 'bg-red-500',
    Water: 'bg-blue-500',
    Electric: 'bg-yellow-400',
    Grass: 'bg-green-500',
    Ice: 'bg-blue-200',
    Fighting: 'bg-red-700',
    Poison: 'bg-purple-500',
    Ground: 'bg-yellow-600',
    Flying: 'bg-indigo-300',
    Psychic: 'bg-pink-500',
    Bug: 'bg-lime-500',
    Rock: 'bg-yellow-800',
    Ghost: 'bg-purple-700',
    Dragon: 'bg-indigo-700',
    Dark: 'bg-gray-700',
    Steel: 'bg-gray-500',
    Fairy: 'bg-pink-300'
  };
  return typeColors[type] || 'bg-gray-400';
}